<template>
    <v-container fluid class="mail-signals-page">
        <v-row align="center" class="mb-4">
            <v-col cols="12" md="7">
                <div class="d-flex align-center">
                    <v-icon color="primary" size="32" class="mr-3">mdi-email-alert-outline</v-icon>
                    <div>
                        <h1 class="text-h5 text-sm-h4 font-weight-medium">Mail Segnalazioni</h1>
                        <p class="body-2 grey--text text--darken-1 mb-0">
                            Trasforma le segnalazioni dei clienti in bozze NC predisposte per l’8D.
                        </p>
                    </div>
                </div>
            </v-col>
            <v-col cols="12" md="5" class="d-flex justify-md-end align-center flex-wrap">
                <v-btn text color="primary" :to="{ name: 'qualita-stampa-odr' }" class="mb-1">
                    <v-icon left>mdi-printer-outline</v-icon>Stampa ODR
                </v-btn>
                <v-chip small outlined color="primary" class="mr-2 mb-1">
                    <v-icon small left>mdi-email-outline</v-icon>
                    {{ publicAddress }}
                </v-chip>
                <v-chip small outlined class="mr-2 mb-1">
                    <v-icon small left>{{ demoMode ? "mdi-flask-outline" : "mdi-shield-check-outline" }}</v-icon>
                    {{ demoMode ? "Demo locale sicura" : "Casella monitorata" }}
                </v-chip>
                <v-btn color="primary" text :loading="loading" class="mb-1" @click="refreshPage">
                    <v-icon left>mdi-refresh</v-icon>
                    Aggiorna
                </v-btn>
            </v-col>
        </v-row>

        <div v-if="messages.length" class="mail-summary mb-4" aria-label="Riepilogo stato casella">
            <div v-for="item in workflowSummary" :key="item.id" class="mail-summary__item">
                <v-icon small :color="item.color" class="mr-2">{{ item.icon }}</v-icon>
                <span class="font-weight-bold mr-1">{{ item.count }}</span>
                <span class="caption">{{ item.label }}</span>
            </div>
        </div>

        <mail-segnalazioni-dashboard
            :metrics="metrics"
            @select-indicator="selectDashboardIndicator"
        />

        <v-alert v-if="errorMessage" type="error" outlined prominent>
            <div class="d-flex flex-column flex-sm-row align-sm-center">
                <div>
                    <div class="font-weight-bold">Impossibile leggere la casella</div>
                    <div>{{ errorMessage }}</div>
                </div>
                <v-spacer />
                <v-btn outlined color="error" class="mt-3 mt-sm-0 mr-sm-2" :loading="loading" @click="loadMessages">
                    <v-icon left>mdi-refresh</v-icon>Riprova
                </v-btn>
                <v-btn
                    v-if="canConfigure && !localPreview"
                    outlined
                    color="error"
                    class="mt-3 mt-sm-0"
                    @click="openConfiguration"
                >
                    Aggiorna password
                </v-btn>
            </div>
        </v-alert>

        <v-alert v-else-if="!configured" type="warning" outlined>
            <div class="d-flex flex-column flex-sm-row align-sm-center">
                <div>
                    <div class="font-weight-bold">Collega la casella qualità</div>
                    <div>{{ configurationDetail }}</div>
                </div>
                <v-spacer />
                <v-btn
                    v-if="canConfigure && !localPreview"
                    color="warning"
                    class="mt-3 mt-sm-0 mr-sm-2"
                    @click="openConfiguration"
                >
                    <v-icon left>mdi-lock-outline</v-icon>
                    Configura casella
                </v-btn>
                <v-btn outlined color="warning" class="mt-3 mt-sm-0" @click="showDemo">
                    Mostra dati demo
                </v-btn>
            </div>
        </v-alert>

        <v-alert v-else-if="!loading && !messages.length" type="info" outlined>
            <div class="d-flex flex-column flex-sm-row align-sm-center">
                <div>
                    <div class="font-weight-bold">
                        {{ pagination.total ? "Nessuna mail corrisponde ai filtri" : "La casella è collegata ma vuota" }}
                    </div>
                    <div>
                        {{ pagination.total ? "Azzera o modifica i filtri della coda." : `Invia o copia una segnalazione in ${publicAddress}, quindi aggiorna.` }}
                    </div>
                </div>
                <v-spacer />
                <v-btn outlined color="info" class="mt-3 mt-sm-0" @click="showDemo">Mostra esempio</v-btn>
            </div>
        </v-alert>

        <mail-segnalazioni-configuration
            v-model="configurationDialog"
            :account-address="accountAddress"
            :loading="configuring"
            :error-message="configurationError"
            @clear-error="configurationError = ''"
            @save="saveConfiguration"
        />

        <v-alert v-if="prepareResult" type="success" outlined>
            <div class="font-weight-bold">NC verificata e pronta</div>
            <div>{{ prepareResult.detail }}</div>
            <div class="caption mt-1">
                {{ prepareResult.signals.length }} articoli · una sola NC · 8D compilabile qui dopo la creazione
            </div>
        </v-alert>

        <v-alert v-if="creationResult" type="success" outlined>
            <div class="font-weight-bold">{{ creationResult.detail }}</div>
            <div v-for="warning in creationResult.warnings || []" :key="warning" class="caption mt-1">{{ warning }}</div>
            <v-btn text color="success" class="mt-2" @click="scrollToEightD">
                <v-icon left>mdi-file-document-edit-outline</v-icon>Compila 8D qui
            </v-btn>
        </v-alert>

        <v-dialog v-model="confirmCreation" max-width="560">
            <v-card>
                <v-card-title>Creare la NC in Sage?</v-card-title>
                <v-card-text>
                    <p>
                        Verrà creata una sola NC per la mail selezionata, con
                        {{ currentDraft && currentDraft.articles.length }} righe articolo. La mail completa e gli
                        allegati associati resteranno collegati come evidenze.
                    </p>
                    <v-alert v-if="duplicateReviewRequired" type="warning" text dense>
                        <div class="font-weight-bold mb-1">Verifica le NC simili prima di procedere</div>
                        <div v-for="duplicate in prepareResult.duplicates" :key="duplicate.nc_number">
                            {{ duplicate.nc_number }} · {{ duplicate.reasons.join("; ") }}
                        </div>
                    </v-alert>
                    <v-checkbox
                        v-if="duplicateReviewRequired"
                        v-model="duplicateReviewed"
                        label="Ho verificato le NC simili: questa è una nuova segnalazione"
                        color="warning"
                        hide-details
                    />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="confirmCreation = false">Annulla</v-btn>
                    <v-btn
                        color="success"
                        :loading="creating"
                        :disabled="duplicateReviewRequired && !duplicateReviewed"
                        @click="createNc"
                    >
                        Conferma e crea
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-row dense>
            <v-col cols="12" lg="4" xl="3">
                <mail-segnalazioni-inbox
                    :messages="messages"
                    :selected-uid="selectedMessage ? selectedMessage.uid : ''"
                    :loading="loading"
                    :pagination="pagination"
                    :filters="mailFilters"
                    :operators="operators"
                    @select="selectMessage"
                    @filters-change="applyMailFilters"
                    @page-change="loadMessages"
                />
            </v-col>
            <v-col cols="12" lg="8" xl="9">
                <mail-segnalazione-review
                    :message="selectedMessage"
                    :draft="currentDraft"
                    :article-options="articleOptions"
                    :article-search-loading="articleSearchLoading"
                    :preparing="preparing"
                    :responding="responding"
                    :local-preview="localPreview"
                    :response-result="responseResult"
                    :customer-options="customerOptions"
                    :customer-search-loading="customerSearchLoading"
                    :associating-customer="associatingCustomer"
                    :reanalyzing="reanalyzing"
                    :addresses="addresses"
                    :addresses-loading="addressesLoading"
                    :prepare-result="prepareResult"
                    :creating="creating"
                    :draft-dirty="draftDirty"
                    :draft-restored="draftRestored"
                    :has-previous="selectedMessageIndex > 0"
                    :has-next="selectedMessageIndex >= 0 && selectedMessageIndex < messages.length - 1"
                    :status-changing="statusChanging"
                    :operators="operators"
                    :saving-draft="savingDraft"
                    :conversation-messages="selectedConversationMessages"
                    :nc-rows="ncRows"
                    :nc-rows-loading="ncRowsLoading"
                    :nc-rows-error="ncRowsError"
                    @update-draft="updateDraft"
                    @update-ownership="updateOwnership"
                    @feedback="submitFeedback"
                    @search-customers="searchCustomers"
                    @confirm-customer="confirmCustomer"
                    @search-articles="searchArticles"
                    @open-attachment="openAttachment"
                    @prepare="prepareSignal"
                    @create-nc="confirmCreation = true"
                    @send-response="sendCustomerResponse"
                    @reset-draft="resetCurrentDraft"
                    @select-previous="selectRelativeMessage(-1)"
                    @select-next="selectRelativeMessage(1)"
                    @change-status="changeMessageStatus"
                    @refresh-nc-rows="loadNcRows"
                />
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import MailSegnalazioneReview from "./include/MailSegnalazioneReview.vue";
import MailSegnalazioniConfiguration from "./include/MailSegnalazioniConfiguration.vue";
import MailSegnalazioniInbox from "./include/MailSegnalazioniInbox.vue";
import MailSegnalazioniDashboard from "./include/MailSegnalazioniDashboard.vue";
import { buildMailboxConfigurationPayload } from "./include/mailSegnalazioniConfiguration.mjs";
import {
    applyAnalysisToDraft,
    applyCustomerToDraft,
    buildCustomerMappingPayload,
    buildPreparePayload,
    buildServerDraftPayload,
    createMailDraft,
    demoMailMessages,
} from "./include/mailSegnalazioniDraft.mjs";
import {
    clearSessionDraft,
    loadSessionDraft,
    saveSessionDraft,
} from "./include/mailSegnalazioniWorkflow.mjs";

export default {
    name: "MailSegnalazioni",
    components: {
        MailSegnalazioneReview,
        MailSegnalazioniConfiguration,
        MailSegnalazioniInbox,
        MailSegnalazioniDashboard,
    },
    data: () => ({
        loading: false,
        preparing: false,
        responding: false,
        localPreview: true,
        configured: true,
        canConfigure: false,
        accountAddress: "quality@mecom.work",
        configurationDetail: "",
        configurationDialog: false,
        configuring: false,
        configurationError: "",
        publicAddress: "quality@mecomsrl.com",
        messages: [],
        selectedMessage: null,
        currentDraft: null,
        errorMessage: "",
        prepareResult: null,
        responseResult: null,
        articleOptions: [],
        articleSearchLoading: false,
        articleSearchTimer: null,
        customerOptions: [],
        customerSearchLoading: false,
        customerSearchTimer: null,
        associatingCustomer: false,
        reanalyzing: false,
        addresses: [],
        addressesLoading: false,
        confirmCreation: false,
        creating: false,
        creationResult: null,
        duplicateReviewed: false,
        draftDirty: false,
        draftRestored: false,
        draftSaveTimer: null,
        statusChanging: false,
        demoMode: false,
        savingDraft: false,
        pagination: { page: 1, page_size: 25, total: 0, has_more: false },
        mailFilters: { search: "", queue: "work", status: "all", assigned_to: "", overdue: false },
        metrics: {},
        operators: [],
        ncRows: [],
        ncRowsLoading: false,
        ncRowsError: "",
    }),
    mounted() {
        document.title = "Work - Mail Segnalazioni";
        if (this.$route?.query?.demo === "1") this.showDemo();
        else this.loadMessages();
    },
    computed: {
        duplicateReviewRequired() {
            return Boolean(this.prepareResult?.duplicate_review_required && this.prepareResult?.duplicates?.length);
        },
        selectedMessageIndex() {
            if (!this.selectedMessage) return -1;
            return this.messages.findIndex((message) => message.uid === this.selectedMessage.uid);
        },
        workflowSummary() {
            const count = (status) => this.messages.filter((message) => message.workflow_status?.id === status).length;
            return [
                { id: "total", label: "mail", count: this.messages.length, icon: "mdi-email-outline", color: "primary" },
                { id: "customer", label: "cliente da associare", count: count("customer_required"), icon: "mdi-account-alert-outline", color: "warning" },
                { id: "article", label: "articolo da verificare", count: count("article_required"), icon: "mdi-tag-search-outline", color: "deep-orange" },
                { id: "ready", label: "pronte per NC", count: count("ready_for_nc"), icon: "mdi-clipboard-check-outline", color: "info" },
                { id: "done", label: "completate", count: count("nc_created") + count("eight_d_sent") + count("handled"), icon: "mdi-shield-check-outline", color: "success" },
            ];
        },
        selectedConversationMessages() {
            const key = this.selectedMessage?.conversation?.key;
            if (!key) return this.selectedMessage ? [this.selectedMessage] : [];
            return this.messages
                .filter((message) => message.conversation?.key === key)
                .sort((left, right) => new Date(left.sent_at || 0) - new Date(right.sent_at || 0));
        },
    },
    beforeDestroy() {
        window.clearTimeout(this.articleSearchTimer);
        window.clearTimeout(this.customerSearchTimer);
        window.clearTimeout(this.draftSaveTimer);
        this.persistCurrentDraft();
    },
    methods: {
        refreshPage() {
            if (this.demoMode) this.showDemo();
            else this.loadMessages();
        },
        scrollToEightD() {
            this.$nextTick(() => document.getElementById("mail-eight-d-title")?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            }));
        },
        updateDraft(draft) {
            this.currentDraft = draft;
            this.markDraftDirty();
            this.prepareResult = null;
            this.duplicateReviewed = false;
        },
        markDraftDirty() {
            this.draftDirty = true;
            window.clearTimeout(this.draftSaveTimer);
            this.draftSaveTimer = window.setTimeout(this.persistCurrentDraft, 500);
        },
        async loadMessages(page = 1) {
            const requestedPage = Number.isFinite(Number(page)) ? Math.max(1, Number(page)) : 1;
            this.loading = true;
            this.errorMessage = "";
            this.prepareResult = null;
            try {
                const response = await this.$work.get(
                    "/qualita/mail-segnalazioni/",
                    {
                        page: requestedPage,
                        page_size: this.pagination.page_size || 25,
                        ...this.mailFilters,
                        overdue: this.mailFilters.overdue ? 1 : 0,
                    },
                    false,
                    null,
                    { ignoreGlobalErrorStatuses: [502], resolveErrorStatuses: [502] }
                );
                const payload = response?.data || {};
                this.configured = payload.configured !== false;
                this.canConfigure = payload.can_configure === true;
                this.accountAddress = payload.account_address || this.accountAddress;
                this.configurationDetail = payload.detail || "";
                this.publicAddress = payload.public_address || this.publicAddress;
                this.localPreview = payload.local_preview !== false;
                this.pagination = { ...this.pagination, ...(payload.pagination || {}), page: requestedPage };
                this.metrics = payload.metrics || {};
                this.operators = Array.isArray(payload.operators) ? payload.operators : [];
                if (response?.status === 502) {
                    this.errorMessage = payload.detail || "Connessione IMAP non riuscita.";
                    this.messages = [];
                    this.selectMessage(null);
                    return;
                }
                this.messages = Array.isArray(payload.messages) ? payload.messages : [];
                const stillSelected = this.messages.find((item) => item.uid === this.selectedMessage?.uid);
                const firstPending = this.messages.find((item) => !["nc_created", "eight_d_sent", "handled"].includes(item.workflow_status?.id));
                this.selectMessage(stillSelected || firstPending || this.messages[0] || null);
            } catch (error) {
                this.metrics = { available: false, scope: "Aggiornamento non riuscito · dati non disponibili" };
                this.errorMessage = error?.response?.data?.detail || "Backend locale non raggiungibile.";
            } finally {
                this.loading = false;
            }
        },
        openConfiguration() {
            this.configurationError = "";
            this.configurationDialog = true;
        },
        async saveConfiguration(password) {
            this.configuring = true;
            this.configurationError = "";
            try {
                const response = await this.$work.post(
                    "/qualita/mail-segnalazioni/configurazione/",
                    buildMailboxConfigurationPayload(password),
                    true
                );
                this.configurationDialog = false;
                this.$toast.success(response?.data?.detail || "Casella collegata.", {
                    icon: "mdi-check-circle-outline",
                    timeout: 4000,
                });
                await this.loadMessages();
            } catch (error) {
                const payload = error?.response?.data || {};
                this.configurationError = payload.detail || payload.password?.[0] || "Verifica della casella non riuscita.";
            } finally {
                this.configuring = false;
            }
        },
        selectMessage(message) {
            this.persistCurrentDraft();
            this.selectedMessage = message;
            this.currentDraft = message ? createMailDraft(message) : null;
            const stored = message?.record?.nc_number ? null : loadSessionDraft(window.sessionStorage, message?.uid);
            if (stored?.draft) this.currentDraft = stored.draft;
            this.draftRestored = Boolean(stored);
            this.draftDirty = false;
            this.prepareResult = null;
            this.responseResult = null;
            this.articleOptions = [];
            this.customerOptions = [];
            this.addresses = [];
            this.prepareResult = null;
            this.creationResult = null;
            this.confirmCreation = false;
            this.duplicateReviewed = false;
            this.ncRows = [];
            this.ncRowsError = "";
            if (message?.record?.nc_number) {
                this.creationResult = {
                    detail: `Mail già collegata alla NC ${message.record.nc_number}.`,
                    nc_number: message.record.nc_number,
                    warnings: [],
                };
                this.loadNcRows(message);
            }
            if (this.currentDraft?.customerConfirmed) {
                this.loadAddresses(this.currentDraft.customerCode);
                if (message?.analysis?.deep_scan !== true && !String(message.uid).startsWith("demo-")) {
                    this.reanalyze(this.currentDraft.customerCode).catch((error) => {
                        this.$toast.warning(
                            error?.response?.data?.detail || "Analisi approfondita degli allegati non disponibile.",
                            { icon: "mdi-alert-outline", timeout: 5000 }
                        );
                    });
                }
            }
        },
        async persistCurrentDraft() {
            window.clearTimeout(this.draftSaveTimer);
            if (!this.currentDraft || this.selectedMessage?.record?.nc_number) return;
            const draft = this.currentDraft;
            const message = this.selectedMessage;
            saveSessionDraft(window.sessionStorage, draft);
            this.draftDirty = false;
            if (!message || String(message.uid).startsWith("demo-")) return;
            this.savingDraft = true;
            try {
                const response = await this.$work.post(
                    "/qualita/mail-segnalazioni/bozza/",
                    buildServerDraftPayload(message, draft),
                    true
                );
                if (message.uid === this.selectedMessage?.uid && response?.data?.mail_draft) {
                    message.mail_draft = response.data.mail_draft;
                }
            } catch (error) {
                this.$toast.error(error?.response?.data?.detail || "Bozza condivisa non salvata.", {
                    icon: "mdi-cloud-alert-outline",
                    timeout: 4000,
                });
            } finally {
                this.savingDraft = false;
            }
        },
        updateOwnership({ assignedTo, slaDueAt }) {
            if (!this.currentDraft) return;
            this.currentDraft = { ...this.currentDraft, assignedTo, slaDueAt };
            this.markDraftDirty();
        },
        async submitFeedback(feedback) {
            if (!this.selectedMessage || !feedback?.kind) return;
            const payload = {
                uid: this.selectedMessage.uid,
                conversation_key: this.selectedMessage.conversation?.key || "",
                ...feedback,
            };
            if (String(this.selectedMessage.uid).startsWith("demo-")) {
                this.$toast.success("Feedback dimostrativo acquisito.", { icon: "mdi-check-decagram-outline", timeout: 2500 });
                return;
            }
            try {
                await this.$work.post("/qualita/mail-segnalazioni/feedback/", payload, true);
                this.$toast.success("Feedback registrato.", {
                    icon: "mdi-check-decagram-outline",
                    timeout: 3000,
                });
            } catch (error) {
                this.$toast.error(error?.response?.data?.detail || "Feedback non registrato.", {
                    icon: "mdi-alert-outline",
                    timeout: 3500,
                });
            }
        },
        applyMailFilters(filters) {
            this.mailFilters = { ...this.mailFilters, ...filters };
            if (this.demoMode) {
                const completed = (message) => ["nc_created", "eight_d_sent", "handled"].includes(message.workflow_status?.id);
                const query = String(this.mailFilters.search || "").toLocaleLowerCase("it");
                this.messages = demoMailMessages().filter((message) => {
                    const queueMatch = this.mailFilters.queue === "all" || (this.mailFilters.queue === "completed" ? completed(message) : !completed(message));
                    const statusMatch = this.mailFilters.status === "all" || message.workflow_status?.id === this.mailFilters.status;
                    const searchMatch = !query || [message.subject, message.snippet, message.sender?.name, message.sender?.email].join(" ").toLocaleLowerCase("it").includes(query);
                    const assignedMatch = !this.mailFilters.assigned_to || (this.mailFilters.assigned_to === "unassigned" ? !message.mail_draft?.assigned_to : String(message.mail_draft?.assigned_to?.id || "") === String(this.mailFilters.assigned_to));
                    const overdueMatch = !this.mailFilters.overdue || message.mail_draft?.sla_overdue === true;
                    return queueMatch && statusMatch && searchMatch && assignedMatch && overdueMatch;
                });
                return;
            }
            this.loadMessages(1);
        },
        selectDashboardIndicator(indicator) {
            // Global KPI drilldowns must not retain a narrower manual search.
            const all = { search: "", queue: "work", status: "all", assigned_to: "", overdue: false };
            if (indicator === "active") this.applyMailFilters(all);
            if (indicator === "overdue") this.applyMailFilters({ ...all, overdue: true });
            if (indicator === "unassigned") this.applyMailFilters({ ...all, assigned_to: "unassigned" });
            if (indicator === "completed") this.applyMailFilters({ ...all, queue: "completed" });
        },
        resetCurrentDraft() {
            if (!this.selectedMessage) return;
            clearSessionDraft(window.sessionStorage, this.selectedMessage.uid);
            this.currentDraft = createMailDraft(this.selectedMessage);
            this.prepareResult = null;
            this.draftRestored = false;
            this.draftDirty = false;
            this.$toast.info("Bozza ripristinata ai dati originali della mail.", { icon: "mdi-restore", timeout: 3500 });
        },
        selectRelativeMessage(offset) {
            const target = this.messages[this.selectedMessageIndex + offset];
            if (target) this.selectMessage(target);
        },
        showDemo() {
            this.demoMode = true;
            this.localPreview = true;
            this.configured = true;
            this.messages = demoMailMessages();
            this.pagination = { page: 1, page_size: 25, total: 2, has_more: false };
            this.operators = [{ id: 1, name: "Responsabile Qualità" }, { id: 2, name: "Marco Rossi" }];
            this.metrics = {
                active_drafts: 7,
                overdue: 2,
                unassigned: 3,
                automatic_recognition_rate: 84.6,
                average_take_charge_hours: 3.4,
                nc_created: 12,
                scope: "demo locale · ultimi 30 giorni",
            };
            this.selectMessage(this.messages[0]);
            this.errorMessage = "";
        },
        searchArticles(term) {
            window.clearTimeout(this.articleSearchTimer);
            if (String(term || "").trim().length < 2) {
                this.articleOptions = [];
                return;
            }
            this.articleSearchTimer = window.setTimeout(async () => {
                this.articleSearchLoading = true;
                try {
                    const response = await this.$work.get("/qualita/mail-segnalazioni/articoli/", {
                        term,
                        customer_code: this.currentDraft?.customerCode || "",
                    });
                    this.articleOptions = (response?.data || []).map((article) => ({
                        ...article,
                        label: `${article.code} · ${article.description}`,
                    }));
                } finally {
                    this.articleSearchLoading = false;
                }
            }, 300);
        },
        searchCustomers(term) {
            window.clearTimeout(this.customerSearchTimer);
            if (String(term || "").trim().length < 2) {
                this.customerOptions = [];
                return;
            }
            this.customerSearchTimer = window.setTimeout(async () => {
                this.customerSearchLoading = true;
                try {
                    const response = await this.$work.get("/qualita/mail-segnalazioni/clienti/", { term });
                    this.customerOptions = response?.data || [];
                } finally {
                    this.customerSearchLoading = false;
                }
            }, 300);
        },
        async confirmCustomer({ customer, contactEmail, contactName }) {
            if (!this.selectedMessage || !customer || !contactEmail) return;
            this.associatingCustomer = true;
            this.prepareResult = null;
            this.creationResult = null;
            try {
                const response = await this.$work.post(
                    "/qualita/mail-segnalazioni/clienti/",
                    buildCustomerMappingPayload(customer, contactEmail, contactName)
                );
                const confirmed = response.data.customer;
                this.currentDraft = applyCustomerToDraft(this.currentDraft, confirmed, contactEmail);
                this.markDraftDirty();
                this.selectedMessage.customer_context = {
                    ...(this.selectedMessage.customer_context || {}),
                    customer: confirmed,
                    candidates: [confirmed],
                    requires_confirmation: false,
                };
                await this.loadAddresses(confirmed.code);
                await this.reanalyze(confirmed.code);
                this.$toast.success(`${response.data.detail} Articoli rianalizzati sulle consegne recenti.`, {
                    icon: "mdi-account-check-outline",
                    timeout: 4000,
                });
            } catch (error) {
                this.$toast.error(error?.response?.data?.detail || "Impossibile associare il cliente.", {
                    icon: "mdi-alert",
                    timeout: 5000,
                });
            } finally {
                this.associatingCustomer = false;
            }
        },
        async reanalyze(customerCode) {
            this.reanalyzing = true;
            try {
                const response = await this.$work.post("/qualita/mail-segnalazioni/analizza/", {
                    uid: this.selectedMessage.uid,
                    customer_code: customerCode,
                });
                const analysis = response.data.analysis;
                this.selectedMessage.analysis = analysis;
                this.selectedMessage.workflow_status = analysis.articles?.length
                    ? { id: "ready_for_nc", label: "Pronta per NC", color: "blue" }
                    : { id: "article_required", label: "Articolo da verificare", color: "deep-orange" };
                this.currentDraft = applyAnalysisToDraft(this.currentDraft, this.selectedMessage, analysis);
                this.markDraftDirty();
                this.articleOptions = [];
            } finally {
                this.reanalyzing = false;
            }
        },
        workflowStatusForMessage(message) {
            if (!message?.customer_context?.customer) {
                return { id: "customer_required", label: "Cliente da associare", color: "orange" };
            }
            if (!message?.analysis?.articles?.length) {
                return { id: "article_required", label: "Articolo da verificare", color: "deep-orange" };
            }
            return { id: "ready_for_nc", label: "Pronta per NC", color: "blue" };
        },
        async changeMessageStatus(change) {
            if (!this.selectedMessage || !change?.action) return;
            this.statusChanging = true;
            try {
                let payload;
                if (String(this.selectedMessage.uid).startsWith("demo-")) {
                    const handled = ["discard", "mark_handled"].includes(change.action);
                    payload = {
                        preview: true,
                        record: {
                            status: handled ? "handled" : "draft",
                            handled_reason: handled ? change.reason : "",
                            handled_note: handled ? change.note || "" : "",
                            handled_at: handled ? new Date().toISOString() : null,
                            nc_number: "",
                        },
                        workflow_status: handled
                            ? { id: "handled", label: "Scartata", color: "grey" }
                            : this.workflowStatusForMessage(this.selectedMessage),
                        detail: handled ? "Mail rimossa dalla coda di lavoro." : "Mail riaperta.",
                    };
                } else {
                    const response = await this.$work.post(
                        "/qualita/mail-segnalazioni/stato/",
                        { uid: this.selectedMessage.uid, ...change },
                        true
                    );
                    payload = response.data;
                }
                const message = this.selectedMessage;
                message.record = payload.record;
                message.workflow_status = change.action === "reopen"
                    ? this.workflowStatusForMessage(message)
                    : payload.workflow_status;
                if (["discard", "mark_handled"].includes(change.action)) {
                    clearSessionDraft(window.sessionStorage, message.uid);
                    const nextPending = this.messages.find((item) => item.uid !== message.uid && !["nc_created", "eight_d_sent", "handled"].includes(item.workflow_status?.id));
                    if (nextPending) this.selectMessage(nextPending);
                }
                this.$toast.success(payload.detail, { icon: "mdi-email-check-outline", timeout: 4000 });
            } catch (error) {
                this.$toast.error(error?.response?.data?.detail || "Impossibile aggiornare lo stato della mail.", {
                    icon: "mdi-alert",
                    timeout: 5000,
                });
            } finally {
                this.statusChanging = false;
            }
        },
        async loadAddresses(customerCode) {
            this.addressesLoading = true;
            try {
                const response = await this.$work.get(`/arts/clienti/${encodeURIComponent(customerCode)}/indirizzi/`);
                if (this.currentDraft?.customerCode !== customerCode) return;
                this.addresses = response?.data || [];
                if (this.addresses.length === 1) {
                    this.currentDraft = { ...this.currentDraft, addressCode: this.addresses[0].codice };
                    this.markDraftDirty();
                }
            } finally {
                this.addressesLoading = false;
            }
        },
        async loadNcRows(message = this.selectedMessage) {
            if (!message?.record?.nc_number) {
                this.ncRows = [];
                this.ncRowsError = "";
                return;
            }
            const selectedUid = String(message.uid);
            const requestedUid = String(message.record.source_uid || message.uid);
            this.ncRowsLoading = true;
            this.ncRowsError = "";
            try {
                if (requestedUid.startsWith("demo-")) {
                    this.ncRows = (this.currentDraft?.articles || []).map((article, index) => ({
                        numero: message.record.nc_number,
                        riga: String((index + 1) * 1000),
                        articolo: article.code,
                        descrizione: article.description || "",
                        eight_d_available: false,
                        status_8d: "Da compilare",
                    }));
                    return;
                }
                const response = await this.$work.get(
                    `/qualita/mail-segnalazioni/${encodeURIComponent(requestedUid)}/righe-nc/`
                );
                if (String(this.selectedMessage?.uid) !== selectedUid) return;
                this.ncRows = response?.data?.rows || [];
            } catch (error) {
                if (String(this.selectedMessage?.uid) !== selectedUid) return;
                this.ncRows = [];
                this.ncRowsError = error?.response?.data?.detail || "Impossibile caricare le righe dell’8D.";
            } finally {
                if (String(this.selectedMessage?.uid) === selectedUid) this.ncRowsLoading = false;
            }
        },
        async openAttachment(attachment) {
            if (!this.selectedMessage || String(this.selectedMessage.uid).startsWith("demo-")) {
                this.$toast.info("Allegato dimostrativo: sarà apribile con una mail reale.", {
                    icon: "mdi-information-outline",
                    timeout: 3500,
                });
                return;
            }
            try {
                const response = await this.$work.get(
                    `/qualita/mail-segnalazioni/${encodeURIComponent(this.selectedMessage.uid)}/allegati/${encodeURIComponent(attachment.id)}/`,
                    {},
                    false,
                    null,
                    { responseType: "blob" }
                );
                const url = URL.createObjectURL(response.data);
                const link = document.createElement("a");
                link.href = url;
                link.target = "_blank";
                link.rel = "noopener";
                link.click();
                window.setTimeout(() => URL.revokeObjectURL(url), 60000);
            } catch (error) {
                this.$toast.error("Impossibile aprire l’allegato", { icon: "mdi-alert", timeout: 5000 });
            }
        },
        async prepareSignal() {
            if (!this.selectedMessage || !this.currentDraft) return;
            this.preparing = true;
            try {
                if (String(this.selectedMessage.uid).startsWith("demo-")) {
                    this.prepareResult = {
                        detail: "Bozza dimostrativa validata localmente. La casella reale non è stata modificata.",
                        signals: this.currentDraft.articles,
                        duplicates: [],
                        duplicate_review_required: false,
                        eight_d_prefill: {
                            notice: "Bozza basata sui soli dati ricevuti; cause e azioni restano da verificare.",
                            d1: { coordinator: "Operatore Qualità", functions: ["Qualità"] },
                            d2: this.currentDraft.articles.map((article) => ({
                                article_code: article.code,
                                what: article.problemText,
                                when: "Segnalazione del 2026-08-29",
                                extent: `${article.quantity || "da indicare"} PZ`,
                                last_delivery: article.lastDelivery,
                            })),
                        },
                    };
                    return;
                }
                const response = await this.$work.post(
                    "/qualita/mail-segnalazioni/prepara/",
                    buildPreparePayload(this.selectedMessage, this.currentDraft),
                    true
                );
                this.prepareResult = response.data;
                this.duplicateReviewed = false;
                this.creationResult = null;
            } catch (error) {
                const payload = error?.response?.data || {};
                const detail = Array.isArray(payload.errors) ? payload.errors.join(" · ") : payload.detail;
                this.$toast.error(detail || "La NC non è ancora completa.", { icon: "mdi-alert", timeout: 6000 });
            } finally {
                this.preparing = false;
            }
        },
        async createNc() {
            if (!this.selectedMessage || !this.currentDraft || !this.prepareResult) return;
            this.creating = true;
            try {
                const payload = {
                    ...buildPreparePayload(this.selectedMessage, this.currentDraft),
                    confirm: true,
                    duplicate_reviewed: this.duplicateReviewed,
                };
                const response = await this.$work.post("/qualita/mail-segnalazioni/crea-nc/", payload, true);
                this.confirmCreation = false;
                if (response.data.preview) {
                    this.$toast.info(response.data.detail, { icon: "mdi-information-outline", timeout: 5000 });
                    return;
                }
                this.creationResult = response.data;
                if (response.data.nc_number) {
                    clearSessionDraft(window.sessionStorage, this.selectedMessage.uid);
                    this.draftDirty = false;
                    this.draftRestored = false;
                    this.selectedMessage.record = {
                        status: "created",
                        nc_number: response.data.nc_number,
                        source_uid: this.selectedMessage.uid,
                    };
                    this.selectedMessage.workflow_status = { id: "nc_created", label: "NC creata", color: "green" };
                    await this.loadNcRows(this.selectedMessage);
                }
                this.prepareResult = null;
                this.$toast.success(response.data.detail, { icon: "mdi-check-circle", timeout: 5000 });
            } catch (error) {
                const payload = error?.response?.data || {};
                const detail = Array.isArray(payload.errors) ? payload.errors.join(" · ") : payload.detail;
                this.$toast.error(detail || "La NC non è stata creata.", { icon: "mdi-alert", timeout: 6000 });
            } finally {
                this.creating = false;
            }
        },
        async sendCustomerResponse(responseDraft) {
            if (!this.selectedMessage) return;
            this.responding = true;
            this.responseResult = null;
            try {
                if (String(this.selectedMessage.uid).startsWith("demo-")) {
                    this.responseResult = {
                        sent: false,
                        preview: true,
                        kind: responseDraft.kind,
                        to: this.selectedMessage.sender?.email || "",
                        subject: responseDraft.subject,
                        attachment_filename: responseDraft.attachment?.name || "",
                        detail: "Anteprima dimostrativa validata: nessuna email è stata inviata.",
                    };
                    return;
                }
                const formData = new FormData();
                formData.append("uid", this.selectedMessage.uid);
                formData.append("kind", responseDraft.kind);
                formData.append("subject", responseDraft.subject);
                formData.append("body", responseDraft.body);
                if (responseDraft.attachment) formData.append("attachment", responseDraft.attachment);
                (responseDraft.evidenceAttachmentIds || []).forEach((id) =>
                    formData.append("evidence_attachment_ids", id)
                );
                const response = await this.$work.post(
                    "/qualita/mail-segnalazioni/risposta/",
                    formData,
                    false,
                    { "Content-Type": "multipart/form-data" }
                );
                this.responseResult = response.data;
                if (response.data.sent && responseDraft.kind === "send_8d") {
                    this.selectedMessage.workflow_status = { id: "eight_d_sent", label: "8D inviato", color: "teal" };
                    if (this.selectedMessage.record) this.selectedMessage.record.status = "eight_d_sent";
                }
            } catch (error) {
                const detail = error?.response?.data?.detail || "Impossibile preparare la risposta al cliente.";
                this.$toast.error(detail, { icon: "mdi-alert", timeout: 5000 });
            } finally {
                this.responding = false;
            }
        },
    },
};
</script>

<style scoped>
.mail-signals-page { max-width: 1800px; }
.mail-summary { display: flex; flex-wrap: wrap; gap: 8px; }
.mail-summary__item { display: inline-flex; align-items: center; min-height: 36px; padding: 6px 10px; border: 1px solid rgba(0, 0, 0, 0.12); border-radius: 4px; background: var(--v-background-base); }
@media (max-width: 599px) {
    .mail-signals-page { padding: 12px; }
}
</style>
